import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell, Loader, Center } from '@mantine/core';
import { useAuth } from './store/auth';
import Nav from './components/Nav';
import ConnectionLostOverlay from './components/ConnectionLostOverlay';
import Login from './routes/Login';
import TechniciansPage from './routes/technicians/TechniciansPage';
import EquipmentPage from './routes/equipment/EquipmentPage';
import ConsumablesPage from './routes/consumables/ConsumablesPage';
import AccessoriesPage from './routes/accessories/AccessoriesPage';
import OrdersPage from './routes/orders/OrdersPage';
import OrderDispatch from './routes/orders/OrderDispatch';
import CustomersPage from './routes/customers/CustomersPage';
import StatisticsPage from './routes/statistics/StatisticsPage';
import AdminPage from './routes/admin/AdminPage';
import ForcePasswordChange from './components/ForcePasswordChange';
import LessonsPage from './routes/lessons/LessonsPage';
import LessonOverlay from './lessons/LessonOverlay';
import TechnicalDocs from './routes/docs/TechnicalDocs';

function Protected({ children }: { children: React.ReactNode }) {
  const { me, loaded } = useAuth();
  const location = useLocation();
  if (!loaded) return <Center h="100vh"><Loader /></Center>;
  if (!me) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  if (me?.role !== 'admin') return <Navigate to="/orders" replace />;
  return <>{children}</>;
}

export default function App() {
  const { fetchMe, loaded } = useAuth();
  useEffect(() => { if (!loaded) fetchMe(); }, [fetchMe, loaded]);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <Protected>
              <AppShell header={{ height: 60 }} padding="md">
                <AppShell.Header>
                  <Nav />
                </AppShell.Header>
                <AppShell.Main>
                  <Routes>
                    <Route path="/" element={<Navigate to="/orders" replace />} />
                    <Route path="/orders" element={<OrdersPage />} />
                    <Route path="/orders/new" element={<OrderDispatch />} />
                    <Route path="/orders/:id" element={<OrderDispatch />} />
                    <Route path="/orders/:id/setup" element={<OrderDispatch />} />
                    <Route path="/lab/technicians" element={<TechniciansPage />} />
                    <Route path="/lab/equipment" element={<EquipmentPage />} />
                    <Route path="/lab/consumables" element={<ConsumablesPage />} />
                    <Route path="/lab/accessories" element={<AccessoriesPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/statistics" element={<StatisticsPage />} />
                    <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
                    <Route path="/user-guide" element={<LessonsPage />} />
                    <Route path="/lessons" element={<Navigate to="/user-guide" replace />} />
                    <Route path="/technical-docs" element={<TechnicalDocs />} />
                    <Route path="*" element={<Navigate to="/orders" replace />} />
                  </Routes>
                </AppShell.Main>
              </AppShell>
              <LessonOverlay />
            </Protected>
          }
        />
      </Routes>
      <ConnectionLostOverlay />
      <ForcePasswordChange />
    </>
  );
}
